import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  useVerifyCredentialsMutation,
  useVerifyCredentialsOtpMutation,
} from '../store/authApi';
import OtpInput from '../components/common/OtpInput';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import ChangePasswordModal from '../components/common/ChangePasswordModal';
import { Arrow } from '../components/home/Hero';
import {
  UI, EASE, wrap, hXl, hM, lead, eyebrow, gate, roles, roleCard, roleCardOpen,
  roleTitle, flagGrey, fieldLabel, input, btnLg, btnYellow,
  textbtn, iconbtn, spin, okmark, alertBad, ico as icoTile, mono, num,
} from '../styles/tl5';

const Icon = ({ d, size = 20 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: size, height: size, minWidth: size, display: 'block' }}
  >
    {d}
  </Box>
);

const PersonIcon = <Icon d={<><circle cx="12" cy="8" r="4" /><path d="M5 21a7 7 0 0 1 14 0" /></>} />;
const StethIcon = <Icon d={<><path d="M6 3v6a5 5 0 0 0 10 0V3" /><path d="M4 3h3M15 3h3M11 14v2a5 5 0 0 0 10 0v-1" /><circle cx="21" cy="12" r="2" /></>} />;
const FlaskIcon = <Icon d={<><path d="M9 3h6M10 3v6L5 19a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 19l-5-10V3" /><path d="M7.5 15h9" /></>} />;
const BriefcaseIcon = <Icon d={<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></>} />;
const LockIcon = <Icon d={<><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>} size={11} />;
const ShieldIcon = <Icon d={<path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />} size={22} />;
const PhoneIcon = <Icon d={<><rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18h2" /></>} size={22} />;
const TickIcon = (
  <Box component="svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: 34, height: 34, display: 'block' }}>
    <path d="M20 6 9 17l-5-5" />
  </Box>
);
const EyeIcon = <Icon d={<><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>} size={18} />;
const EyeOffIcon = <Icon d={<><path d="M3 3l18 18" /><path d="M10.6 6.2A9.9 9.9 0 0 1 12 5c6.4 0 10 7 10 7a18 18 0 0 1-3.2 4M6.2 7.6A17.7 17.7 0 0 0 2 12s3.6 7 10 7a9.6 9.6 0 0 0 4-.85" /></>} size={18} />;

// One card per portal we support. `requires` is the user_type that must
// match for the card to be enabled. `individual` is special — every
// signed-in user can open their personal dashboard regardless of role.
const PORTALS = [
  {
    id:       'individual',
    title:    'Individual',
    desc:     'See your personal lab reports, trends, upcoming tests and bookings.',
    icon:     PersonIcon,
    route:    ROUTES.DASHBOARD,
    requires: null,                 // always open
  },
  {
    id:       'partner',
    title:    'Medical Professional',
    desc:     'View patient reports referred to you, raise queries, and access your clinical dashboard.',
    icon:     StethIcon,
    route:    ROUTES.MED_DASHBOARD,
    // Both centre-admin partners AND doctor-mode sessions land on
    // the Medical Portal, so either user_type unlocks this card.
    requires: ['partner', 'doctor'],
  },
  {
    id:       'hospital',
    title:    'Hospital / Lab',
    desc:     'Process patient samples, manage requisitions, and review your lab queue.',
    icon:     FlaskIcon,
    route:    ROUTES.LAB_DASHBOARD,
    requires: 'hospital',
  },
  {
    id:       'organization',
    title:    'Corporate',
    desc:     "Manage your company's employee wellness reports, health programs, and analytics.",
    icon:     BriefcaseIcon,
    route:    ROUTES.CORP_DASHBOARD,
    requires: 'organization',
  },
];

// Friendly "why is this locked" toast copy, keyed by the card the user
// tried to open. Keep it short — toast trunctates aggressively.
const LOCKED_COPY = {
  partner:      "You're not registered as a partner — this portal is for medical professionals.",
  hospital:     "You're not registered as a hospital / lab — this portal is for lab staff.",
  organization: "You're not registered as an organization — this portal is for corporate accounts.",
};

// What the hero calls the signed-in account.
const ROLE_LABEL = {
  patient: 'Individual',
  partner: 'Medical Professional',
  doctor: 'Medical Professional',
  hospital: 'Hospital / Lab',
  organization: 'Corporate',
};

const Reports = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, setSession, uhidVerified } = useAuth();
  const { showToast } = useUI();

  // Patient-only UHID + password gate. Patients hit this page from
  // the navbar's "My Reports" link; before showing report data we
  // ask them to confirm their UHID + password, then enter the OTP
  // we send to their registered phone. Doctors / lab partners /
  // corporates skip the gate — their dashboards are role-gated
  // already.
  // The reference is four exclusive screens, not one page: the role cards,
  // the UHID gate, the OTP gate, then a success card. Opening the Individual
  // card moves a patient into the gate rather than jumping to the dashboard,
  // which also closes a hole — the old layout let a patient click straight
  // past the verification it was showing underneath.
  const [view, setView] = useState('roles'); // 'roles' | 'uhid' | 'otp' | 'done'
  const [uhid, setUhid] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [otp, setOtp] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [authErr, setAuthErr] = useState('');
  // Pops the forced change-password modal when the OTP step returns
  // must_change_password === false (i.e. patient hasn't set their
  // own password yet — still on the default 'trustlab@2026' assigned
  // by the eDOS sync). Field semantics: false = not changed yet,
  // true = changed.
  const [showChangePwd, setShowChangePwd] = useState(false);

  const [verifyCredentials,    { isLoading: verifying }]    = useVerifyCredentialsMutation();
  const [verifyCredentialsOtp, { isLoading: verifyingOtp }] = useVerifyCredentialsOtpMutation();

  // Redirect to login if not signed in (bookmark / direct URL case).
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?next=${encodeURIComponent(ROUTES.REPORTS)}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Decide synchronously (during render) whether to skip this page
  // entirely and bounce the user to their portal. Using a declarative
  // <Navigate> below avoids the flicker that a useEffect-based
  // redirect causes (form paints once, then disappears).
  //
  // Cases that skip the gate (all require uhidVerified for the session):
  //   patient + has UHID → /dashboard
  //   partner / doctor   → /portal/medical
  //   hospital           → /portal/lab
  //   organization       → /portal/corporate
  //
  // Patient also requires user.uhid to actually exist — uhidVerified
  // can persist in localStorage past an admin unlinking, and without
  // a UHID the dashboard has nothing to show.
  const skipGateTarget =
    !isAuthenticated || !uhidVerified
      ? null
    : (user?.user_type === 'patient' && user?.uhid)
      ? ROUTES.DASHBOARD
    : (user?.user_type === 'partner' || user?.user_type === 'doctor')
      ? ROUTES.MED_DASHBOARD
    : (user?.user_type === 'hospital')
      ? ROUTES.LAB_DASHBOARD
    : (user?.user_type === 'organization')
      ? ROUTES.CORP_DASHBOARD
      : null;

  // Decorate the static PORTALS list with a per-user `enabled` flag so
  // the card visuals + click handlers both know whether the user has
  // access. Individual is always enabled; role cards match their
  // owner's user_type — `requires` is either a single string or an
  // array of acceptable types (the Medical Professional card uses an
  // array so both 'partner' and 'doctor' unlock it).
  const portals = useMemo(() => {
    if (!user) return [];
    return PORTALS.map((p) => {
      const req = p.requires;
      const enabled = req === null
        || (Array.isArray(req) ? req.includes(user.user_type) : user.user_type === req);
      return { ...p, enabled };
    });
  }, [user]);

  if (!user) return null;

  // Already verified → skip the gate. Returns the redirect during
  // render so the role-picker / verification form never paints.
  //
  // Except on the success screen: verifying calls `setSession`, which sets
  // uhidVerified and would make this redirect fire on the very next render,
  // so the card the user just earned never appeared. The skip is meant for
  // a *fresh* visit by an already-verified user, not for the moment they
  // finish verifying.
  if (skipGateTarget && view !== 'done') {
    return <Navigate to={skipGateTarget} replace />;
  }

  const isPatient = user.user_type === 'patient';

  const onCardClick = (p) => {
    if (!p.enabled) {
      showToast(LOCKED_COPY[p.id] || 'You do not have access to this portal.', 'error');
      return;
    }
    // Patients must clear the UHID gate before their reports open. Every
    // other role is already gated by its own portal, so it goes straight in.
    if (p.id === 'individual' && isPatient) {
      setView('uhid');
      return;
    }
    navigate(p.route);
  };

  // Step 1: validate UHID + password. Server checks the password and
  // dispatches a 6-digit OTP to the registered phone. We don't get a
  // session yet — only `requiresOtp + masked_phone`. Step 2 below
  // completes the login.
  const onUhidSubmit = async (e) => {
    e?.preventDefault?.();
    setAuthErr('');
    if (!uhid.trim()) return setAuthErr('Enter your UHID');
    if (!pwd)         return setAuthErr('Enter your password');

    try {
      const res = await verifyCredentials({
        identifier: uhid.trim(),
        password: pwd,
      }).unwrap();
      setMaskedPhone(res.masked_phone || '');
      setView('otp');
      showToast(`OTP sent to ${res.masked_phone || 'your registered mobile'}`);
    } catch (err) {
      setAuthErr(err?.data?.message || 'Could not verify UHID. Please try again.');
    }
  };

  // Step 2: verify the OTP for the same UHID's registered phone. On
  // success we get the JWT, mark uhidVerified so future visits to
  // /reports skip both steps and bounce straight to /dashboard.
  const onOtpSubmit = async (e) => {
    e?.preventDefault?.();
    setAuthErr('');
    if (!/^\d{6}$/.test(otp.trim())) return setAuthErr('Enter the 6-digit OTP');

    try {
      const res = await verifyCredentialsOtp({
        identifier: uhid.trim(),
        otp: otp.trim(),
      }).unwrap();
      setSession({ token: res.token, user: res.user, uhidVerified: true });
      // Patient hasn't set their own password yet → pop the modal.
      // Strict `=== false` so a legacy account where the field was
      // never written (undefined) skips the dialog.
      if (res.must_change_password === false) {
        setShowChangePwd(true);
        return;
      }
      showToast(`Welcome back, ${res.user.name}`);
      setView('done');
    } catch (err) {
      setAuthErr(err?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  const roleName = ROLE_LABEL[user.user_type] || 'Individual';

  // Each state gets its own heading and sub-line, as the reference does.
  const HEAD = {
    roles: ['Role-based access', 'Your TrustLab, your way.'],
    uhid:  ['Secure access', 'Verify it’s you.'],
    otp:   ['Secure access', 'Verify it’s you.'],
    done:  ['Secure access', 'You’re verified.'],
  }[view];

  // The gate panels share a shell: icon, heading, copy, then the form.
  const Gate = ({ icon, title, children, back }) => (
    <Box sx={gate}>
      {back}
      <Box sx={{ ...icoTile, width: 48, height: 48, marginBottom: 0, borderRadius: '16px' }}>{icon}</Box>
      <Typography component="h2" sx={{ ...hM, mt: '8px' }}>{title}</Typography>
      {children}
    </Box>
  );

  return (
    <Box>
      {/* ═══ HERO ═══ */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--tl-g9)',
          color: '#fff',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),'
              + 'linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            pointerEvents: 'none',
          },
        }}
      >
        {/* Reference padding — the deep bottom is what the cards overlap. */}
        <Box
          sx={{
            ...wrap,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            paddingBlock: 'clamp(48px, 6vw, 80px) clamp(110px, 10vw, 150px)',
          }}
        >
          <Box component="span" sx={{ ...eyebrow, background: 'rgba(255,255,255,.1)', color: 'var(--tl-teal-l)' }}>
            {HEAD[0]}
          </Box>
          <Typography component="h1" sx={{ ...hXl, color: '#fff' }}>{HEAD[1]}</Typography>
          {view === 'roles' && (
            <Typography sx={{ ...lead, color: '#B6CFC8', maxWidth: 640 }}>
              You&rsquo;re signed in as <Box component="b" sx={{ color: '#fff' }}>{roleName}</Box>.
              {' '}Other portals stay locked for your account.
            </Typography>
          )}
        </Box>
      </Box>

      {/* ═══ THE FOUR STATES — one at a time ═══ */}
      <Box
        component="section"
        sx={{
          marginTop: 'clamp(-110px, -8vw, -70px)',
          position: 'relative',
          paddingBottom: 'clamp(56px, 7vw, 96px)',
        }}
      >
        <Box sx={wrap}>
          {/* ── roles ── */}
          {view === 'roles' && (
            <Box sx={roles}>
              {portals.map((p, i) => (
                <Box
                  key={p.id}
                  component="button"
                  type="button"
                  onClick={() => onCardClick(p)}
                  aria-disabled={!p.enabled}
                  sx={{
                    ...(p.enabled ? roleCardOpen : roleCard),
                    cursor: 'pointer',
                    animation: `tl-stgin .55s ${EASE} both`,
                    animationDelay: `${i * 70}ms`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <Box
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        width: 44,
                        height: 44,
                        flex: 'none',
                        borderRadius: '14px',
                        background: p.enabled ? 'rgba(95,224,198,.14)' : 'var(--tl-mint)',
                        color: p.enabled ? 'var(--tl-teal-l)' : 'var(--tl-muted)',
                      }}
                    >
                      {p.icon}
                    </Box>
                    {!p.enabled && (
                      <Box component="span" sx={flagGrey}>{LockIcon}Locked</Box>
                    )}
                  </Box>

                  <Box component="b" sx={{ ...roleTitle, color: p.enabled ? '#fff' : 'var(--tl-g9)' }}>
                    {p.title}
                  </Box>
                  <Typography sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.6, color: p.enabled ? '#B6CFC8' : 'var(--tl-muted)' }}>
                    {p.desc}
                  </Typography>

                  {p.enabled ? (
                    <Box component="span" sx={{ ...btnYellow, mt: 'auto', alignSelf: 'flex-start', pointerEvents: 'none' }}>
                      Open
                      <Arrow size={16} />
                    </Box>
                  ) : (
                    <Box component="span" sx={{ mt: 'auto', fontFamily: UI, fontSize: 12.5, color: 'var(--tl-muted)' }}>
                      Not available for your account
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* ── uhid ── */}
          {view === 'uhid' && (
            <Gate
              icon={ShieldIcon}
              title="Verify your UHID"
              back={(
                <Box
                  component="button"
                  type="button"
                  onClick={() => { setView('roles'); setAuthErr(''); }}
                  sx={{ ...textbtn, display: 'inline-flex', alignItems: 'center', gap: '6px', alignSelf: 'flex-start', px: 0, color: 'var(--tl-g6)' }}
                >
                  <Arrow size={16} dir="left" />
                  All portals
                </Box>
              )}
            >
              <Typography sx={{ fontFamily: UI, fontSize: 15.5, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                Reports contain protected health information, so we check it&rsquo;s you.
              </Typography>

              {authErr && <Box sx={alertBad} role="alert">{authErr}</Box>}

              <Box component="form" onSubmit={onUhidSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '18px', mt: '8px' }}>
                <Box>
                  <Box component="label" htmlFor="rp-u" sx={fieldLabel}>UHID</Box>
                  <Box
                    component="input"
                    id="rp-u"
                    value={uhid}
                    onChange={(e) => setUhid(e.target.value)}
                    placeholder="UH-XXXXXX"
                    autoComplete="username"
                    sx={{ ...input, ...mono, fontSize: 15 }}
                  />
                </Box>

                <Box>
                  <Box component="label" htmlFor="rp-p" sx={fieldLabel}>Password</Box>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="input"
                      id="rp-p"
                      type={showPwd ? 'text' : 'password'}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      autoComplete="current-password"
                      sx={{ ...input, pr: '56px' }}
                    />
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                      sx={{ ...iconbtn, position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}
                    >
                      {showPwd ? EyeOffIcon : EyeIcon}
                    </Box>
                  </Box>
                </Box>

                <Box component="button" type="submit" disabled={verifying} sx={{ ...btnLg, width: '100%', '&:disabled': { opacity: 0.55, pointerEvents: 'none' } }}>
                  {verifying && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
                  {verifying ? 'Checking…' : 'Continue'}
                </Box>
              </Box>

              <Typography sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted)', textAlign: 'center', mt: '4px' }}>
                Don&rsquo;t have a UHID yet? Visit any TrustLab centre with a valid ID to get one.
              </Typography>
            </Gate>
          )}

          {/* ── otp ── */}
          {view === 'otp' && (
            <Gate icon={PhoneIcon} title="Enter the OTP">
              <Typography sx={{ fontFamily: UI, fontSize: 15.5, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                Sent to your registered mobile{' '}
                <Box component="b" sx={{ ...num, color: 'var(--tl-g9)' }}>{maskedPhone || 'number'}</Box>
              </Typography>

              {authErr && <Box sx={alertBad} role="alert">{authErr}</Box>}

              <Box component="form" onSubmit={onOtpSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '20px', mt: '8px' }}>
                <OtpInput value={otp} onChange={setOtp} disabled={verifyingOtp} autoFocus />
                <Box component="button" type="submit" disabled={verifyingOtp} sx={{ ...btnYellow, minHeight: 58, width: '100%' }}>
                  {verifyingOtp && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
                  {verifyingOtp ? 'Verifying…' : 'Verify & continue'}
                </Box>
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() => { setView('uhid'); setOtp(''); setAuthErr(''); }}
                sx={{ ...textbtn, alignSelf: 'center' }}
              >
                Re-enter password
              </Box>
            </Gate>
          )}

          {/* ── done ── */}
          {view === 'done' && (
            <Box sx={{ ...gate, alignItems: 'center', textAlign: 'center' }}>
              <Box sx={okmark}>{TickIcon}</Box>
              <Typography component="h2" sx={{ ...hM, mt: '8px' }}>
                Welcome — your reports are ready
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => navigate(ROUTES.DASHBOARD)}
                sx={{ ...btnLg, mt: '8px' }}
              >
                Open My Reports
                <Arrow size={16} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Forced password change — patients still on the eDOS-assigned
          'trustlab@2026' password. Once they submit a new
          password the modal closes and we navigate forward. */}
      <ChangePasswordModal
        open={showChangePwd}
        onSuccess={() => {
          setShowChangePwd(false);
          showToast('Welcome — your reports are ready');
          navigate(ROUTES.DASHBOARD);
        }}
      />
    </Box>
  );
};

export default Reports;
